import { useEffect, useState } from 'react';

import { useDrawer } from '../context/DrawerContext';
import {
	MYWALLET_INTEGRATION_NAME,
	MYWALLET_OPEN_EVENT
} from '../utils/myCarsIntegration';
import useWebIntegrationConfig from './useWebIntegrationConfig';

/**
 * useMyWalletCta -- whether the drawer shows a My Wallet button, and what a
 * click does.
 *
 * Two independent yeses are required, and both fail closed:
 *
 *   1. *Should it show?* `showHeaderPlacement === true` in the site's My
 *      Wallet config. That field is the WIC tool's "Show Header Placement"
 *      checkbox -- what support flips when a dealer asks to have their My
 *      Wallet icon removed -- so this button respects it. A site without the
 *      integration has no config at all, which covers "not installed" too.
 *   2. *Can it work?* `window.caiEventBus` exists, meaning the wallet
 *      integration has loaded and is listening for 'wallet:open'. Never treat
 *      this as a substitute for (1): the bus is shared with uds:* traffic and
 *      disappears when the DealStarter app is off (DE551567), so it proves
 *      capability, never intent.
 *
 * The bus is probed in an effect rather than during render, so a browser-only
 * global can't disagree with the server-rendered markup. It is re-probed on
 * every open/close because the wallet integration loads 2.7-3.9s after this
 * widget does -- a mount-only probe always saw nothing, which is why the
 * button never appeared at all before US2059854.
 *
 * No greyed-out state, unlike useMyCarsCta: a missing signal renders nothing,
 * so a shopper never sees a control that can't do anything. `isDisabled` stays
 * in the return shape only to match useMyCarsCta, which really computes it.
 *
 * @returns {{isEnabled: boolean, isDisabled: boolean, handleClick: () => void}}
 */
const useMyWalletCta = () => {
	const { isOpen, closeDrawer } = useDrawer();
	const [isWalletBusAvailable, setIsWalletBusAvailable] = useState(false);
	const { integration } = useWebIntegrationConfig(MYWALLET_INTEGRATION_NAME);
	const isHeaderPlacementEnabled =
		integration?.fields?.showHeaderPlacement === true;

	useEffect(() => {
		setIsWalletBusAvailable(typeof window.caiEventBus !== 'undefined');
	}, [isOpen]);

	const handleClick = () => {
		if (isOpen) closeDrawer();
		window.caiEventBus?.emit(MYWALLET_OPEN_EVENT);
	};

	return {
		isEnabled: isHeaderPlacementEnabled && isWalletBusAvailable,
		isDisabled: false,
		handleClick
	};
};

export default useMyWalletCta;
